import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { BagTransportTermApiService } from '../bag-transport-term-api.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { catchError, finalize, map } from 'rxjs';
import {
	CheckListStatus,
	TransportTermCheckList,
	TransportTermCheckListPayload,
} from '@/codebook/check-list/check-list.model';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';

interface TransportTermCheckListByGroup {
	groupName: string;
	checkLists: (TransportTermCheckList & { selected: boolean })[];
}

@Component({
	selector: 'app-check-list-dialog',
	standalone: true,
	imports: [
		MatCheckboxModule,
		MatDialogModule,
		MatButtonModule,
		MatMenuModule,
		ReactiveFormsModule,
		MatProgressBarModule,
		MatInputModule,
		FormsModule,
		NgClass,
		MatProgressSpinnerModule,
		MatDatepickerModule,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
		{ provide: DateAdapter, useClass: PolishDateAdapter },
	],
	templateUrl: './check-list-dialog.component.html',
	styleUrl: './check-list-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckListDialogComponent implements OnInit {
	readonly termId = inject(MAT_DIALOG_DATA);

	readonly checkLists = signal<(TransportTermCheckList & { selected: boolean })[]>([]);
	readonly isLoading = signal(false);
	readonly checkListToAddName = signal<string | null>(null);

	readonly checkListsByGroup = computed(() => this.groupCheckListsByGroup(this.checkLists()));

	readonly itemsDoneProgress = computed(
		() => this.itemsSelectedOrRejectedCount() * this.percentValueOfOneCheckList(),
	);

	readonly itemsSelectedOrRejectedCount = computed(
		() => this.checkLists().filter((item) => item.selected || item.status === CheckListStatus.REJECTED).length,
	);

	readonly percentValueOfOneCheckList = computed(() =>
		this.checkLists().length ? (1 / this.checkLists().length) * 100 : 0,
	);

	readonly statuses = Object.values(CheckListStatus);
	readonly CheckListStatus = CheckListStatus;

	readonly dialogRef = inject(MatDialogRef);
	readonly deadlineShouldBeReloaded = signal(false);

	private readonly noteOriginalValue = new Map<number, string>();
	private readonly transportTermApi = inject(BagTransportTermApiService);
	private readonly datePipe = inject(DatePipe);

	ngOnInit(): void {
		this.isLoading.set(true);
		this.transportTermApi
			.getTransportTermCheckList(this.termId)
			.pipe(
				map((result) => result.results),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((checkLists) => {
				this.checkLists.set(
					checkLists.map((item) => ({ ...item, selected: item.status === CheckListStatus.DONE })),
				);
			});
	}

	updateSelected(isSelected: boolean, id: number): void {
		const previousState = this.checkLists();

		const nextStatus = isSelected ? CheckListStatus.DONE : CheckListStatus.TODO;

		this.checkLists.update((list) =>
			list.map((item) =>
				item.id === id
					? {
							...item,
							selected: isSelected,
							status: nextStatus,
						}
					: item,
			),
		);

		this.updateCheckListStatus(id, nextStatus, previousState);
	}

	updateStatus(id: number, status: CheckListStatus): void {
		const previousState = this.checkLists();

		this.checkLists.update((list) => {
			return list.map((item) => ({
				...(item.id === id
					? {
							...item,
							status,
							selected: status === CheckListStatus.DONE,
						}
					: item),
			}));
		});

		this.updateCheckListStatus(id, status, previousState);
	}

	importCheckLists(): void {
		this.isLoading.set(true);
		this.transportTermApi
			.importTransportTermCheckList(this.termId)
			.pipe(
				map((result) => result.results),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((checkLists) => {
				this.checkLists.set(
					checkLists.map((item) => ({ ...item, selected: item.status === CheckListStatus.DONE })),
				);
			});
	}

	addCheckListItem(): void {
		if (!this.checkListToAddName()) {
			return;
		}
		const newItem: TransportTermCheckListPayload = {
			transportTerm: this.termId,
			name: this.checkListToAddName()!,
			groupName: '',
			status: CheckListStatus.TODO,
			note: '',
			deadline: null,
		};

		this.transportTermApi.createTransportTermCheckList(this.termId, newItem).subscribe((createdItem) => {
			this.checkLists.update((list) => [...list, { ...createdItem, selected: false }]);
			this.checkListToAddName.set(null);
		});
	}

	deleteCheckListItem(id: number): void {
		this.transportTermApi.deleteTransportTermCheckList(this.termId, id).subscribe(() => {
			this.checkLists.update((list) => list.filter((item) => item.id !== id));
		});
	}

	onNoteFocus(id: number, current: string): void {
		this.noteOriginalValue.set(id, current ?? '');
	}

	onNoteBlur(id: number, note: string): void {
		const original = this.noteOriginalValue.get(id) ?? '';

		if (original === (note ?? '')) {
			this.noteOriginalValue.delete(id);
			return;
		}

		this.noteOriginalValue.delete(id);

		this.transportTermApi
			.patchTransportTermCheckList(this.termId, id, { note: note })
			.pipe(
				catchError((err) => {
					this.checkLists.update((list) =>
						list.map((item) => (item.id === id ? { ...item, note: original } : item)),
					);

					return err;
				}),
			)
			.subscribe();
	}

	onDeadlineChange(checkListId: number, newDate: Date): void {
		const oldValue = this.checkLists().find((i) => i.id === checkListId)?.deadline;
		if (oldValue === undefined) {
			return;
		}

		this.updateCheckListDeadline(checkListId, oldValue, this.datePipe.transform(newDate, 'YYYY-MM-dd'));
	}

	private groupCheckListsByGroup(
		checkLists: (TransportTermCheckList & { selected: boolean })[],
	): TransportTermCheckListByGroup[] {
		const grouped = checkLists.reduce(
			(acc, item) => {
				if (!acc[item.groupName]) {
					acc[item.groupName] = {
						groupName: item.groupName,
						checkLists: [],
					};
				}
				acc[item.groupName].checkLists.push(item);
				return acc;
			},
			{} as Record<string, TransportTermCheckListByGroup>,
		);

		Object.values(grouped).forEach((group) => {
			group.checkLists.sort((a, b) => a.id - b.id);
		});

		return Object.values(grouped).sort((a, b) => {
			if (!a.groupName && b.groupName) return 1;
			if (a.groupName && !b.groupName) return -1;
			return a.groupName.localeCompare(b.groupName);
		});
	}

	private updateCheckListStatus(
		checkListId: number,
		status: CheckListStatus,
		previousState: (TransportTermCheckList & { selected: boolean })[],
	): void {
		this.transportTermApi
			.patchTransportTermCheckList(this.termId, checkListId, {
				status,
			})
			.pipe(
				catchError((err) => {
					this.checkLists.set(previousState);
					return err;
				}),
			)
			.subscribe(() => {
				this.deadlineShouldBeReloaded.set(true);
			});
	}

	private updateCheckListDeadline(checkListId: number, oldDate: string, deadline: string): void {
		this.transportTermApi
			.patchTransportTermCheckList(this.termId, checkListId, {
				deadline,
			})
			.pipe(
				catchError((err) => {
					this.checkLists.update((list) =>
						list.map((item) => (item.id === checkListId ? { ...item, deadline: oldDate } : item)),
					);
					return err;
				}),
			)
			.subscribe(() => {
				this.deadlineShouldBeReloaded.set(true);
			});
	}
}
